import React, { memo, useEffect } from 'react';
import { DefaultSectionHeader } from 'layouts/Section/SectionHeader';
import { CampaignSectionContainer, CampaignSectionBody } from './components';
import LoadingIndicator from '../../../../shared/components/LoadingIndicator';
import {
  useCampaignActions,
  type CampaignSource,
  type CampaignTypes,
} from 'features/campaigns';
import type { CampaignSectionStrategy, CampaignSectionProps, CampaignSectionConfig } from './types';
import { useAppStore } from 'store/app/appStore';

import styles from './MultiViewCampaignSection.module.css';

interface CampaignSectionWrapperProps extends CampaignSectionProps {
  strategy: CampaignSectionStrategy<CampaignSource>;
  config?: CampaignSectionConfig;
}

/**
 * Internal component that uses the strategy hook
 * Separated to ensure hooks are called at the right level
 */
const MultiViewCampaignSection: React.FC<CampaignSectionWrapperProps> = ({
  data,
  isActive = false,
  strategy,
  config = { showPanel: true, showHeader: true },
}) => {
  // Use the strategy's hook to get section state
  const {
    isLoading,
    isError,
    panelInfo,
    items,
    isSingleItem,
    activeCampaign,
    handleViewToggle,
    containerProps,
    itemCount,
  } = strategy.useCampaignSection(data, isActive);

  const { setSelectedCampaignId, setCampaignType } = useCampaignActions();
  const campaignType: CampaignTypes = strategy.type as CampaignTypes;
  const campaignOnlyType = useAppStore((state) => state.settings.campaignOnly?.type);

  useEffect(() => {
    activeCampaign?.id && setSelectedCampaignId(activeCampaign?.id, campaignType)
  }, [activeCampaign?.id, setSelectedCampaignId, campaignType]);

  // Claim the global campaign type while this section is active, so type-derived
  // resolution (CountdownTimer, and panel/score lookup via useCampaignStatus)
  useEffect(() => {
    // In ingame campaign-only mode only the opened type may claim the global
    // campaignType, so a coexisting off-screen section doesn't flip it.
    if (isActive && (!campaignOnlyType || campaignOnlyType === campaignType)) {
      setCampaignType(campaignType);
    }
  }, [isActive, campaignType, setCampaignType, campaignOnlyType]);

  // Early return conditions - hide section when appropriate
  if (items.length === 0 || isError) return null;

  // Loading state in single view
  if (isLoading) {
    return (
      <CampaignSectionContainer sectionName={data.name} isActive={isActive} additionalProps={containerProps}>
        <div className={styles.loadingContainer}>
          <LoadingIndicator show />
        </div>
      </CampaignSectionContainer>
    );
  }

  const showSingleItem = isSingleItem && activeCampaign;

  if (itemCount === 0 ) return null;

  return (
      <CampaignSectionContainer sectionName={data.name} isActive={isActive} additionalProps={containerProps}>
        {config.showHeader && (
          <DefaultSectionHeader
            name={data.name}
            isActive={isActive}
            handleViewToggle={handleViewToggle}
            gamesCount={itemCount}
          />
        )}
        <CampaignSectionBody
          sectionName={data.name}
          isActive={isActive}
          backgroundImage={panelInfo.backgroundImage}
          backgroundColor={panelInfo.backgroundColor}
          additionalProps={containerProps}
          isSingleItem={isSingleItem}
        >
          {config.showPanel && strategy.renderPanel?.(panelInfo)}

          {showSingleItem
            ? strategy.renderSingleItemLayout?.({ item: activeCampaign, data, isActive })
            : strategy.renderMultiItemsLayout?.({ items, name: data.name, isActive })
          }
        </CampaignSectionBody>
      </CampaignSectionContainer>
  );
};


export default memo(MultiViewCampaignSection);
